import { apiJson } from '@/api/client'
import { closedStatusForResolution } from './types'
import type {
  AddNotePayload,
  AlertDetail,
  AlertFilters,
  AlertNote,
  AlertsListResponse,
  BulkAssignPayload,
  BulkClosePayload,
  BulkEscalatePayload,
  ClosePayload,
  SavedFilter,
  SortKey,
} from './types'

export interface ListArgs {
  filters?: AlertFilters
  sort?: SortKey[]
  page?: number
  pageSize?: number
}

/**
 * Converts a filter date to the RFC3339 instant the backend's `from`/`to`
 * params require. Accepts either a full timestamp (re-serialised to UTC) or
 * a bare `YYYY-MM-DD` day from a date input, interpreted in the operator's
 * local timezone: `from` -> start of that day, `to` -> end of that day, so
 * an inclusive day range behaves as the operator expects. Returns undefined
 * for unparseable input so a bad value is dropped rather than sent.
 */
export function toRfc3339Bound(value: string, edge: 'start' | 'end'): string | undefined {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  let d: Date
  if (day) {
    const [, y, m, dd] = day
    d =
      edge === 'start'
        ? new Date(Number(y), Number(m) - 1, Number(dd), 0, 0, 0, 0)
        : new Date(Number(y), Number(m) - 1, Number(dd), 23, 59, 59, 999)
  } else {
    d = new Date(value)
  }
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/**
 * Builds the `filters` object for `POST /alerts/export`. The Go export
 * runner (internal/cases/exports AlertExportFilters) decodes the same
 * AlertFilters JSON keys but parses `date_from` / `date_to` with
 * time.RFC3339, so the bare `YYYY-MM-DD` values held by the date inputs
 * must be widened to full instants (start / end of local day) exactly as
 * the list endpoint's `from` / `to` are. Unparseable dates are dropped.
 */
export function buildExportFilters(filters: AlertFilters): AlertFilters {
  // Strip anything the server's DisallowUnknownFields decoder would reject.
  const known: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && EXPORT_FILTER_KEYS.includes(k)) known[k] = v
  }
  const { date_from, date_to, ...rest } = known as AlertFilters
  const out: AlertFilters = { ...rest }
  const from = date_from ? toRfc3339Bound(date_from, 'start') : undefined
  const to = date_to ? toRfc3339Bound(date_to, 'end') : undefined
  if (from) out.date_from = from
  if (to) out.date_to = to
  if (Array.isArray(out.assigned_to) && out.assigned_to.length > MAX_ASSIGNED_TO) {
    out.assigned_to = out.assigned_to.slice(0, MAX_ASSIGNED_TO)
  }
  return out
}

/**
 * Accepted `filters` keys for POST /alerts/export. MUST equal the JSON keys
 * of exports.AlertExportFilters in internal/cases/exports/alerts_csv.go (decoded with DisallowUnknownFields,
 * parsed by internal/cases/alerts/listfilter/listfilter.go). Parity is
 * asserted in __tests__/exportFilters.test.ts.
 */
export const EXPORT_FILTER_KEYS: readonly string[] = [
  'status',
  'priority',
  'channel',
  'scenario',
  'queue_id',
  'assigned_to',
  'sla_at_risk',
  'sla_breached',
  'amount_min_minor',
  'amount_max_minor',
  'date_from',
  'date_to',
  'q',
  'unlinked',
]

/**
 * Server cap on repeated `assigned_to` values (listfilter.MaxAssignedTo,
 * internal/cases/alerts/listfilter/listfilter.go). Semantics are ANY: an alert
 * matches if it is assigned to ANY listed user (the literal "unassigned"
 * additionally matches unassigned alerts). More than the cap is a 400, so
 * the client truncates instead of sending a request that cannot succeed.
 */
export const MAX_ASSIGNED_TO = 50

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Returns the assigned_to values the export endpoint would reject
 * (it accepts a user public_id UUID, a UUID array, or the literal
 * "unassigned"; numeric ids are no longer accepted). Empty == valid.
 */
export function invalidExportAssignees(filters: AlertFilters): string[] {
  const a = filters.assigned_to
  if (a === undefined || a === 'unassigned') return []
  return a.filter((v) => !UUID_RE.test(v))
}

/**
 * Serialises list args onto the `GET /alerts` wire contract:
 * `from`/`to` (RFC3339), `sla_breach`, repeatable `status` / `channel`
 * (wire channel vocabulary), `sort=field:dir`, `q`, `amount_*_minor`.
 */
export function buildSearchParams({
  filters = {},
  sort = [],
  page = 1,
  pageSize = 50,
}: ListArgs) {
  const sp = new URLSearchParams()
  sp.set('page', String(page))
  sp.set('page_size', String(pageSize))
  for (const s of sort) sp.append('sort', `${String(s.field)}:${s.dir}`)
  for (const v of filters.status ?? []) sp.append('status', v)
  for (const v of filters.priority ?? []) sp.append('priority', v)
  for (const v of filters.scenario ?? []) sp.append('scenario', v)
  for (const v of filters.queue_id ?? []) sp.append('queue_id', v)
  for (const v of filters.channel ?? []) sp.append('channel', v)
  if (filters.assigned_to === 'unassigned') sp.append('assigned_to', 'unassigned')
  // Repeated params (never comma-joined), ANY semantics, capped at the server max.
  else for (const v of (filters.assigned_to ?? []).slice(0, MAX_ASSIGNED_TO)) sp.append('assigned_to', v)
  if (filters.sla_at_risk) sp.set('sla_at_risk', 'true')
  if (filters.sla_breached) sp.set('sla_breach', 'true')
  if (filters.amount_min_minor !== undefined)
    sp.set('amount_min_minor', String(filters.amount_min_minor))
  if (filters.amount_max_minor !== undefined)
    sp.set('amount_max_minor', String(filters.amount_max_minor))
  const from = filters.date_from ? toRfc3339Bound(filters.date_from, 'start') : undefined
  if (from) sp.set('from', from)
  const to = filters.date_to ? toRfc3339Bound(filters.date_to, 'end') : undefined
  if (to) sp.set('to', to)
  if (filters.q) sp.set('q', filters.q)
  return sp
}

// Wire-shape translator: case-service envelopes its list responses as
// `{items, total, ...}` (the platform-wide convention for paginated
// lists) but the React side has historically modeled this as
// `{alerts, total, ...}`. Translate at the adapter so Go and React
// can each keep their canonical shapes — flipping either would
// either break the rest of the platform's list endpoints (Go) or
// cascade through every alert-related component (React).
type AlertsListWire =
  | AlertsListResponse
  | (Omit<AlertsListResponse, 'alerts'> & { items: AlertsListResponse['alerts'] })

export async function listAlerts(args: ListArgs): Promise<AlertsListResponse> {
  const sp = buildSearchParams(args)
  const wire = await apiJson<AlertsListWire>(`/alerts?${sp.toString()}`)
  if ('alerts' in wire && Array.isArray(wire.alerts)) {
    return wire
  }
  if ('items' in wire && Array.isArray(wire.items)) {
    const { items, ...rest } = wire
    return { ...rest, alerts: items }
  }
  // Defensive fallback so a bare `{total: 0}` envelope (no array at
  // all) doesn't crash useAlertsInfinite's `p.alerts.length` reduce.
  const fallback: AlertsListResponse = {
    alerts: [],
    total: 0,
    page: args.page ?? 1,
    page_size: args.pageSize ?? 50,
    queue_depths: {},
  }
  return { ...fallback, ...(wire as Partial<AlertsListResponse>) }
}

export async function getAlert(id: string): Promise<AlertDetail> {
  const wire = await apiJson<AlertDetail & { customer?: Partial<AlertDetail['customer_snapshot']> }>(
    `/alerts/${id}`,
  )
  // Wire-shape resilience: the live backend currently returns a slim
  // `customer` object on the detail payload (id + display_name +
  // customer_number) instead of the rich `customer_snapshot` the UI
  // expects. When customer_snapshot is missing, synthesise a
  // best-effort shape from `customer` so the rail and detail page
  // can still render — falling values default to safe placeholders
  // (no risk score, unverified KYC, no PEP, no BVN). When the
  // backend enrichment lands the spread will simply pass through.
  if (!wire.customer_snapshot) {
    const c = wire.customer ?? ({} as Partial<AlertDetail['customer_snapshot']>)
    wire.customer_snapshot = {
      id: c.id ?? '',
      customer_number: c.customer_number ?? '',
      display_name: c.display_name ?? 'Unknown customer',
      risk_score: c.risk_score ?? null,
      risk_score_history: c.risk_score_history ?? [],
      kyc_tier: c.kyc_tier ?? 'unverified',
      pep: c.pep ?? false,
      bvn_masked: c.bvn_masked ?? null,
    }
  }
  return wire
}

export function listSavedFilters(): Promise<{ saved_filters: SavedFilter[] }> {
  return apiJson('/alerts/saved-filters')
}

// Response shape mirrors the real backend's `BulkResult{Affected int}`
// envelope (`internal/api/alerts_handler.go` `BulkAssign`), not
// `{updated}` — same class of drift as `bulkCloseAlerts` below: the
// mock used to agree with a mistyped frontend instead of the server,
// so "Assigned undefined alerts" shipped to real analysts while every
// mocked test stayed green.
export function bulkAssignAlerts(payload: BulkAssignPayload) {
  return apiJson<{ affected: number }>('/alerts/bulk/assign', { method: 'POST', body: payload })
}

// Wire-shape translator: `BulkClosePayload.resolution` carries the
// UI-facing `Resolution` vocabulary ('false_positive', 'duplicate',
// …), but the backend's `BulkCloseRequest.Resolution` field expects
// an `entities.AlertStatus` closed_* terminal ('closed_false_positive',
// 'closed_duplicate', …) and 400s on anything else. This translation
// used to only exist inside the MSW mock, so every mocked test passed
// while every real close request failed. `closedStatusForResolution`
// is the shared translator both this function and mocks.ts import —
// see its doc comment in types.ts.
//
// Response shape also mirrors the real backend's `BulkResult{Affected
// int}` envelope (internal/api/alerts_handler.go), not `{updated}`.
export function bulkCloseAlerts(payload: BulkClosePayload) {
  return apiJson<{ affected: number }>('/alerts/bulk/close', {
    method: 'POST',
    body: {
      alert_ids: payload.alert_ids,
      resolution: closedStatusForResolution(payload.resolution),
      reason: payload.reason,
    },
  })
}

// Response shape mirrors `BulkResult{Affected int}`
// (`internal/api/alerts_handler.go` `BulkEscalate`), not `{updated}` —
// see `bulkAssignAlerts`'s doc comment above.
export function bulkEscalateAlerts(payload: BulkEscalatePayload) {
  return apiJson<{ affected: number }>('/alerts/bulk/escalate', { method: 'POST', body: payload })
}

// Single-alert close — POST /alerts/{id}/close. This is the endpoint
// that enforces the workflow state machine + atomic take-and-close
// server-side; the alert-detail rail must use this instead of the
// bulk endpoint with a one-element `alert_ids` array (the bulk path
// bypassed the state machine entirely — see AF alert-close fix).
export function closeAlert(payload: ClosePayload) {
  return apiJson<{ status: string }>(`/alerts/${payload.alert_id}/close`, {
    method: 'POST',
    body: {
      resolution: closedStatusForResolution(payload.resolution),
      reason: payload.reason,
    },
  })
}

export function addAlertNote(payload: AddNotePayload) {
  return apiJson<{ note: AlertNote; alert_id: string }>(
    `/alerts/${payload.alert_id}/notes`,
    { method: 'POST', body: { body: payload.body } },
  )
}
